import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ClockIcon } from '@heroicons/react/24/outline';

// Redux
import { setOrderDataAction, setOrderTypeAction } from '../../redux/Actions/CreateOrderActions';
import {
  createNewOrder,
  createNewSubscriptions,
  getOrderSummary,
  resetCreateOrderFlow,
} from '../../redux/Reducers/CreateOrderReducer';

// Utils
import { getDifference } from '../../utils/dateTimeFormatter';
import { amountCheckFn } from '../../utils/commonUtil';

// Components
import OrderConfirmation from './OrderConfirmation';
import AddressSection from './V2/AddressSection';
import ServicesSection from './V2/ServicesSection';
import PreferencesSection from './V2/PreferencesSection';
import ScheduleSection from './V2/ScheduleSection';
import PaymentSection, { isPaymentValid } from './V2/PaymentSection';
import SummaryPanel from './V2/SummaryPanel';
import {
  FREQ_OPTIONS,
  SECTION_IDS,
  hasDropoff,
  hasPickup,
  isOk,
  isPickupInstructionMissing,
  scrollToSection,
} from './V2/helpers';

// Images
import lc_onetime from '../../images/lc_onetime_new.png';
import lc_subscription from '../../images/lc_subscription_new.png';
import pl_onetime from '../../images/pl_onetime.png';
import pl_subscription from '../../images/pl_subscription.png';

const tenant = import.meta.env.VITE_TENANT_NAME;
const SESSION_MS = 30 * 60 * 1000;
const NO_RUSH = { is_rush: false };

const ORDER_TYPES = [
  { key: 'oneTime', label: 'One-time', image: tenant === 'pushlaundry' ? pl_onetime : lc_onetime },
  { key: 'subscription', label: 'Subscription', image: tenant === 'pushlaundry' ? pl_subscription : lc_subscription },
];

export default function CreateNewOrderV2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const order = useSelector((s) => s.createNewOrder);
  const {
    orderType,
    orderServiceAddress,
    selectedServices,
    orderTimeSlots,
    orderRepeatFrequency,
    orderProvider,
    orderPaymentDetails,
    orderPreferences,
    orderPromoCode,
    orderIsRush,
    final_tip,
  } = order;
  const { profileData } = useSelector((s) => s.profile);

  const [ready, setReady] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [placeError, setPlaceError] = useState('');
  const [estimate, setEstimate] = useState({});
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [scheduleRefreshKey, setScheduleRefreshKey] = useState(0);

  // ── Guards ────────────────────────────────────────────────────────
  useEffect(() => {
    if (profileData?.status !== 'ACTIVE') {
      toast.error('Your account is inactive!');
      navigate('/dashboard');
    } else if (!profileData?.is_allowed_to_create_order) {
      toast.error('Past due invoice. Make a payment to order.');
      navigate('/account', { state: { from: 'payments' } });
    }
  }, [profileData?.status]);

  // ── Fresh flow on every visit (sections mount only after this) ─────
  useEffect(() => {
    dispatch(resetCreateOrderFlow());
    dispatch(setOrderDataAction({ orderSummaryError: null }));
    const t = new URLSearchParams(location.search).get('type');
    dispatch(setOrderTypeAction(t === 'subscription' ? 'subscription' : 'oneTime'));
    setReady(true);
  }, []);

  const setType = (t) => {
    if (t === orderType) return;
    dispatch(setOrderTypeAction(t));
    // Availability differs between orders and subscriptions.
    dispatch(setOrderDataAction({ orderTimeSlots: null, orderIsRush: NO_RUSH }));
  };

  // ── Session timer: starts once a pickup time is chosen ────────────
  const pickupChosen = hasPickup(orderTimeSlots);
  const [expiresAt, setExpiresAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const warnedRef = useRef(false);

  useEffect(() => {
    if (pickupChosen && !expiresAt) {
      setExpiresAt(Date.now() + SESSION_MS);
      setNow(Date.now());
      warnedRef.current = false;
    } else if (!pickupChosen) setExpiresAt(null);
  }, [pickupChosen]);

  useEffect(() => {
    if (!expiresAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const remaining = expiresAt ? Math.max(0, Math.round((expiresAt - now) / 1000)) : null;
  useEffect(() => {
    if (remaining == null) return;
    if (remaining <= 0) {
      dispatch(setOrderDataAction({ orderTimeSlots: null, orderIsRush: NO_RUSH }));
      setScheduleRefreshKey((k) => k + 1);
      toast.error('Session expired. Please choose your pickup time again.');
    } else if (remaining <= 300 && !warnedRef.current) {
      warnedRef.current = true;
      toast.error('Session expires in 5 minutes. Place your order to keep these times.');
    }
  }, [remaining]);

  // ── Live estimate ─────────────────────────────────────────────────
  const itemsKey = (selectedServices || []).map((s) => `${s.service_id}:${s.quantity}`).join(',');
  useEffect(() => {
    if (!ready || isEmpty(selectedServices) || !orderServiceAddress?.id) {
      setEstimate({});
      setEstimateLoading(false);
      return;
    }
    let stale = false;
    setEstimateLoading(true);
    const timer = setTimeout(() => {
      const payload = {
        items: selectedServices.map((s) => ({ service_id: s.service_id, quantity: s.quantity })),
        ...(orderPromoCode && { promo_code: orderPromoCode }),
        final_tip: final_tip === null ? undefined : final_tip,
        address_id: orderServiceAddress.id,
        is_rush: orderIsRush?.is_rush,
        ...(orderProvider?.id && { worker_id: orderProvider.id }),
      };
      getOrderSummary(payload).then((res) => {
        if (stale) return;
        setEstimateLoading(false);
        if (isOk(res)) {
          setEstimate(res?.data || {});
          dispatch(setOrderDataAction({ orderSummary: res?.data || null, orderSummaryError: null }));
        } else {
          // Most failures here are an invalid promo; drop it and re-estimate without it.
          if (orderPromoCode) setPromoError(res?.message || `"${orderPromoCode}" can't be applied.`);
          dispatch(setOrderDataAction({ orderSummaryError: res?.message, orderPromoCode: null }));
        }
      });
    }, 350);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [
    ready,
    itemsKey,
    orderPromoCode,
    final_tip,
    orderIsRush?.is_rush,
    orderIsRush?.priority_fee,
    orderServiceAddress?.id,
    orderProvider?.id,
  ]);

  // A new promo attempt clears the previous error.
  useEffect(() => {
    if (orderPromoCode) setPromoError('');
  }, [orderPromoCode]);

  // ── What's left before the order can be placed ────────────────────
  const isSub = orderType === 'subscription';
  const missing = [
    !orderServiceAddress && { label: 'Pickup address', id: SECTION_IDS.address },
    isEmpty(selectedServices) && { label: 'Services', id: SECTION_IDS.services },
    isPickupInstructionMissing(orderPreferences) && { label: 'Pickup instructions', id: SECTION_IDS.preferences },
    isSub && !orderRepeatFrequency?.value && { label: 'Frequency', id: 'order-type' },
    !pickupChosen && { label: 'Pickup time', id: SECTION_IDS.schedule },
    pickupChosen && !hasDropoff(orderTimeSlots) && { label: 'Delivery time', id: SECTION_IDS.schedule },
    !isPaymentValid(orderPaymentDetails) && { label: 'Payment method', id: SECTION_IDS.payment },
  ].filter(Boolean);

  // ── Place order ───────────────────────────────────────────────────
  const placeOrder = () => {
    if (missing.length || placing) return;
    setPlacing(true);
    setPlaceError('');
    const common = {
      service_address_id: orderServiceAddress?.id,
      items: selectedServices.map((i) => ({ service_id: i.service_id, quantity: i.quantity })),
      preferred_worker_id: orderProvider?.id || undefined,
      preferences: orderPreferences || undefined,
      promo_code: orderPromoCode,
      ...(final_tip && { final_tip }),
    };
    const request = isSub
      ? createNewSubscriptions(
          {
            ...common,
            subscription_entries: {
              first_pickup_date: `${orderTimeSlots.first_pickup_date}T00:00:00`,
              pickup_start_time: orderTimeSlots.pickup_start_time,
              pickup_end_time: orderTimeSlots.pickup_end_time,
              dropoff_start_time: orderTimeSlots.dropoff_start_time,
              dropoff_end_time: orderTimeSlots.dropoff_end_time,
              subscription_frequency: orderRepeatFrequency?.value,
              days_after: getDifference(orderTimeSlots.first_pickup_date, orderTimeSlots.dropoff_date),
            },
            create_subscription: true,
          },
          'subscription'
        )
      : createNewOrder(
          {
            ...common,
            pickup_date: orderTimeSlots.pickup_date,
            dropoff_date: orderTimeSlots.dropoff_date,
            pickup_start_time: orderTimeSlots.pickup_start_time,
            pickup_end_time: orderTimeSlots.pickup_end_time,
            dropoff_start_time: orderTimeSlots.dropoff_start_time,
            dropoff_end_time: orderTimeSlots.dropoff_end_time,
            create_order: true,
          },
          'ORDER'
        );

    request.then((res) => {
      setPlacing(false);
      if (isOk(res)) {
        toast.success(res?.message || (isSub ? 'Subscription created!' : 'Order created!'));
        dispatch(
          setOrderDataAction(
            isSub ? { newSubscriptionId: res?.subscription_id || null } : { newOrderId: res?.order_id || null }
          )
        );
        setExpiresAt(null);
        setConfirmed(true);
      } else {
        setPlaceError(res?.message || 'Something went wrong. Please try again.');
        toast.error(res?.message || 'Something went wrong');
      }
    });
  };

  if (confirmed)
    return (
      <div className='px-4 py-8 sm:mx-auto sm:max-w-5xl sm:px-6'>
        <OrderConfirmation />
      </div>
    );

  const minutes = remaining != null ? Math.floor(remaining / 60) : 0;
  const seconds = remaining != null ? remaining % 60 : 0;
  const hasItems = !isEmpty(selectedServices);

  return (
    <div className='min-h-screen bg-slate-50/60'>
      <div className='mx-auto max-w-6xl px-4 py-5 sm:px-6 lg:py-6'>
        {/* Header */}
        <div className='mb-4 flex flex-wrap items-start justify-between gap-3'>
          <div>
            <h1 className='text-xl font-bold text-slate-900 sm:text-2xl'>Create new order</h1>
            <p className='text-sm text-slate-500'>Pick your options below. Your total updates as you go.</p>
          </div>
          {remaining != null && (
            <div
              title='Time left to place this order before your selected times are released'
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold tabular-nums ${
                remaining <= 300 ? 'bg-red-50 text-red-600' : 'bg-client-iconbg text-client-300'
              }`}
            >
              <ClockIcon className='h-4 w-4' />
              {minutes}:{seconds.toString().padStart(2, '0')}
            </div>
          )}
        </div>

        {/* Order type */}
        <div id='order-type' className='mb-4 flex scroll-mt-4 flex-wrap items-center gap-x-4 gap-y-2'>
          <div role='radiogroup' aria-label='Order type' className='inline-flex rounded-md bg-slate-200/60 p-1'>
            {ORDER_TYPES.map((t) => {
              const active = orderType === t.key;
              return (
                <button
                  key={t.key}
                  type='button'
                  role='radio'
                  aria-checked={active}
                  onClick={() => setType(t.key)}
                  className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold transition sm:px-4 ${
                    active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <img src={t.image} alt='' className='h-5 w-5 object-contain' />
                  {t.label}
                </button>
              );
            })}
          </div>
          {isSub ? (
            <div className='flex flex-wrap items-center gap-1.5'>
              <span className='text-xs font-medium text-slate-500'>Repeats</span>
              {FREQ_OPTIONS.map((f) => {
                const active = orderRepeatFrequency?.value === f.value;
                return (
                  <button
                    key={f.value}
                    type='button'
                    aria-pressed={active}
                    onClick={() =>
                      dispatch(setOrderDataAction({ orderRepeatFrequency: { value: f.value, name: f.name } }))
                    }
                    className={`rounded-md border px-3 py-1 text-xs font-semibold transition ${
                      active
                        ? 'border-client-300 bg-client-300 text-white'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {f.name}
                  </button>
                );
              })}
            </div>
          ) : (
            <span className='text-xs text-slate-500'>A single pickup and delivery</span>
          )}
        </div>

        {/* Body */}
        {ready && (
          <div className='grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start lg:gap-6 xl:grid-cols-[minmax(0,1fr)_380px]'>
            <div className='min-w-0 space-y-4'>
              <AddressSection />
              <ServicesSection />
              <PreferencesSection />
              <ScheduleSection refreshKey={scheduleRefreshKey} />
              <PaymentSection />
            </div>
            <div className='lg:sticky lg:top-4'>
              <SummaryPanel
                estimate={estimate}
                estimateLoading={estimateLoading}
                promoError={promoError}
                missing={missing}
                onPlace={placeOrder}
                placing={placing}
                placeError={placeError}
              />
            </div>
          </div>
        )}
      </div>

      {/* Mobile action bar (sits above the app's bottom nav) */}
      {ready && (
        <div className='fixed inset-x-0 bottom-16 z-40 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-4px_12px_rgba(15,23,42,0.06)] backdrop-blur lg:hidden'>
          <div className='mx-auto flex max-w-xl items-center gap-3'>
            <button
              type='button'
              className='min-w-0 flex-1 text-left'
              onClick={() => scrollToSection(SECTION_IDS.summary)}
            >
              <p className='text-[11px] text-slate-500'>Estimated total</p>
              <p className='text-lg font-bold leading-tight text-slate-900'>
                {hasItems ? amountCheckFn(estimate?.estimated_total_cost) : '$0.00'}
              </p>
            </button>
            {missing.length ? (
              <button
                type='button'
                onClick={() => scrollToSection(missing[0].id)}
                className='bg-client-text rounded-md px-4 py-2.5 text-sm font-semibold text-white'
              >
                Next: {missing[0].label}
              </button>
            ) : (
              <button
                type='button'
                disabled={placing || estimateLoading}
                onClick={() => scrollToSection(SECTION_IDS.summary)}
                className='bg-client-300 rounded-md px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60'
              >
                Review & place
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
